import { ProviderBoundary } from 'switch-shared/modules/crud/provider-boundary/provider-boundary.entity';
import {
  CreateProviderDto,
  SearchProviderQuery,
  SearchProviderResultDto,
  UpdateProviderDto,
} from 'switch-shared/modules/crud/provider/provider.dto';
import { Provider } from 'switch-shared/modules/crud/provider/provider.entity';
import { CurrentWeather } from 'switch-shared/modules/external/met-no/met-no.dto';
import {
  ProviderNeighbour,
  ProviderProfile,
} from 'switch-shared/modules/features/provider-boundary/provider-boundary.dto';
import { Serialized } from 'switch-shared/types/serialised';

import { baseAPI } from './baseAPI.service';
import { API_METHODS, ENDPOINT_URLS } from './config/apiTags';

export const providerAPI = baseAPI
  .enhanceEndpoints({
    addTagTypes: [ENDPOINT_URLS.PROVIDER],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getAllProviders: builder.query<
        SearchProviderResultDto,
        SearchProviderQuery
      >({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.PROVIDER}`,
            method: API_METHODS.GET,
            params,
          };
        },
        providesTags: (res, _err, _) =>
          res?.data.map((con) => ({
            type: ENDPOINT_URLS.PROVIDER,
            id: con._id,
          })) ?? [],
      }),

      fetchProvider: builder.query<Provider, { id: string }>({
        query: ({ id }) => {
          return {
            method: API_METHODS.GET,
            url: `${ENDPOINT_URLS.PROVIDER}/${id}`,
          };
        },
        providesTags: [ENDPOINT_URLS.PROVIDER],
      }),

      getProviderBoundary: builder.query<
        ProviderBoundary | null,
        { providerId: string; simplify?: boolean }
      >({
        queryFn: async (
          { providerId, simplify },
          _api,
          _extraOptions,
          baseQuery
        ) => {
          const result = await baseQuery({
            method: API_METHODS.GET,
            url: `provider-boundary/by-provider/${providerId}`,
            ...(simplify && { params: { simplify: true } }),
          });

          if (result.error?.status === 404) {
            return { data: null };
          }

          if (result.error) {
            return { error: result.error };
          }

          return {
            data: result.data as ProviderBoundary,
          };
        },
      }),
      lookupProviders: builder.query<Provider[], { lat: number; lng: number }>({
        query: (params) => ({
          method: API_METHODS.GET,
          url: 'provider-boundary/lookup',
          params,
        }),
      }),

      getProviderNeighbours: builder.query<
        ProviderNeighbour[],
        { providerId: string }
      >({
        query: ({ providerId }) => ({
          method: API_METHODS.GET,
          url: `provider-boundary/neighbours/${providerId}`,
        }),
      }),

      getProviderProfile: builder.query<
        Serialized<ProviderProfile>,
        { providerId: string }
      >({
        query: ({ providerId }) => ({
          method: API_METHODS.GET,
          url: `${ENDPOINT_URLS.PROVIDER}/${providerId}/profile`,
        }),
        providesTags: [ENDPOINT_URLS.PROVIDER],
      }),

      getProviderWeather: builder.query<
        CurrentWeather | null,
        { lat: number; lng: number }
      >({
        query: (params) => ({
          method: API_METHODS.GET,
          url: `${ENDPOINT_URLS.PROVIDER}/weather`,
          params,
        }),
        keepUnusedDataFor: 900,
      }),

      createProvider: builder.mutation<Provider, CreateProviderDto>({
        query: (body) => {
          return {
            method: API_METHODS.POST,
            url: `${ENDPOINT_URLS.PROVIDER}`,
            body,
          };
        },
        invalidatesTags: [ENDPOINT_URLS.PROVIDER],
      }),

      updateProvider: builder.mutation<
        Provider,
        { id: string; data: UpdateProviderDto }
      >({
        query: ({ id, data }) => {
          return {
            method: API_METHODS.PATCH,
            url: `${ENDPOINT_URLS.PROVIDER}/${id}`,
            body: data,
          };
        },
        invalidatesTags: [ENDPOINT_URLS.PROVIDER],
      }),
    }),
  });

export const {
  useGetAllProvidersQuery,
  useFetchProviderQuery,
  useGetProviderBoundaryQuery,
  useGetProviderNeighboursQuery,
  useLookupProvidersQuery,
  useGetProviderProfileQuery,
  useGetProviderWeatherQuery,
  useCreateProviderMutation,
  useUpdateProviderMutation,
} = providerAPI;
